import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, TableRow, TableCell } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faFileCsv,
	faFilePdf,
	faPrint,
} from "@fortawesome/free-solid-svg-icons";
import { CSVLink } from "react-csv";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import useStore from "../../store/store";
import accountService from "../../services/account";
import Button from "../../controls/Button";
import style from "../../utils/style";
import utils from "../../utils/utils";
import constants from "../../utils/constants";
import PageHeading from "../../components/PageHeading";
import CustomTable from "../../components/Table";
import moment from "moment";

function Trial() {
	const navigate = useNavigate();
	const csvLinkRef = useRef();
	let { token, company, setErrorMessage, setIsLoading, isLoggedIn } =
		useStore((state) => state);

	const [arr, setArr] = useState([]);
	const [isDone, setIsDone] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const headers = [
		{
			title: "Account",
			align: "left",
			headerAlign: "left",
			formatter: (k) => k.title,
		},
		{
			title: "Debit / بنام",
			align: "right",
			headerAlign: "right",
			formatter: (row) =>
				row.debit !== 0
					? utils.formatToCurrency(Math.abs(row.debit), "")
					: "",
		},
		{
			title: "Credit / جمع",
			align: "right",
			headerAlign: "right",
			formatter: (row) =>
				row.credit !== 0
					? utils.formatToCurrency(Math.abs(row.credit), "")
					: "",
		},
	];

	useEffect(() => {
		if (!isLoggedIn) navigate("/login");
		reload();
	}, []);

	const reload = () => {
		setIsLoading(true);
		setArr([]);
		accountService
			.trialBalance(token, {
				page: page,
				limit: rowsPerPage,
			})
			.then((result) => {
				if (result.error) {
					setErrorMessage(result.error);
					setIsLoading(false);
					return;
				}

				let t = result.data.map((k) => {
					let difference = k.fromSum - k.toSum;
					difference = k.initial
						? (difference -= parseInt(k.initial))
						: difference;
					return {
						...k,
						debit: difference > 0 ? difference : 0,
						credit: difference < 0 ? difference : 0,
					};
				});
				setArr(t);
				setIsLoading(false);
				setIsDone(true);
			});
	};

	const diff = () => {
		return (
			Math.abs(arr.reduce((a, b) => a + b.credit, 0)) -
			Math.abs(arr.reduce((a, b) => a + b.debit, 0))
		);
	};

	const FooterSumRow = () => (
		<>
			{diff() !== 0 && (
				<TableRow style={{ height: 35 }}>
					<TableCell>Cash</TableCell>
					<TableCell
						style={{
							textAlign: "right",
						}}
					>
						{utils.formatToCurrency(diff(), "")}
					</TableCell>
					<TableCell></TableCell>
				</TableRow>
			)}
			<TableRow style={{ height: 35, background: "#eee" }}>
				<TableCell style={{ fontWeight: 500 }}>Total</TableCell>
				<TableCell align="right" style={{ fontWeight: 500 }}>
					{utils.formatToCurrency(
						Math.abs(arr.reduce((a, b) => a + b.debit, 0) + diff()),
						""
					)}
				</TableCell>
				<TableCell align="right" style={{ fontWeight: 500 }}>
					{utils.formatToCurrency(
						Math.abs(arr.reduce((a, b) => a + b.credit, 0)),
						""
					)}
				</TableCell>
			</TableRow>
		</>
	);

	const getCsvData = () => {
		const headers = [
			{ label: "Account Title", key: "title" },
			{ label: "Debit / بنام", key: "debit" },
			{ label: "Credit / جمع", key: "credit" },
		];

		const csvData = [
			headers.map((header) => header.label),
			...arr?.map((item) =>
				headers.map((header) =>
					!isNaN(item[header.key])
						? utils.formatToCurrency(Math.abs(item[header.key]), "")
						: item[header.key]
				)
			),
		];

		let debit = utils.formatToCurrency(
			Math.abs(arr.reduce((a, b) => a + b.debit, 0) + diff()),
			""
		);
		let credit = utils.formatToCurrency(
			Math.abs(arr.reduce((a, b) => a + b.credit, 0)),
			""
		);
		csvData.push(["Cash", utils.formatToNumber(diff()), "0"]);
		csvData.push(["Total", debit, credit]);
		return csvData;
	};

	const handleExport = () => csvLinkRef.current.link.click();

	const createPdf = (isPrint) => {
		let debit = Math.abs(arr.reduce((a, b) => a + b.debit, 0) + diff());
		let credit = Math.abs(arr.reduce((a, b) => a + b.credit, 0));

		const columns = ["Account Titles", "Debit", "Credit"];
		const rows = [
			...arr.map((item) => [
				item.title,
				utils.formatToCurrency(item.debit, ""),
				utils.formatToCurrency(Math.abs(item.credit), ""),
			]),
			["Cash", utils.formatToCurrency(diff(), ""), "0"],
			[
				"Total",
				utils.formatToCurrency(debit, ""),
				utils.formatToCurrency(credit, ""),
			],
		];

		if (debit > credit)
			rows.push([
				"Diffrence",
				utils.formatToCurrency(Math.abs(debit - credit), ""),
				"",
			]);

		if (debit < credit)
			rows.push([
				"Diffrence",
				"",
				utils.formatToCurrency(Math.abs(debit - credit), ""),
			]);

		const doc = new jsPDF();

		let isFirstPage = true;
		doc.autoTable({
			head: [columns],
			body: rows,
			styles: { fontSize: 10, cellPadding: 1, overflow: "linebreak" },
			didDrawPage: (data) => {
				if (isFirstPage) {
					doc.setFillColor(constants.PRIMARY);
					doc.rect(0, 0, doc.internal.pageSize.getWidth(), 35, "F");

					doc.setTextColor(255, 255, 255);
					doc.setFontSize(22);
					doc.text(company.shopName || "", 20, 15);
					doc.setFontSize(16);
					doc.text(company.name || "", 20, 25);
					doc.setFillColor(255, 255, 255);
					doc.setTextColor(0, 0, 0);
					doc.text("Trial Balance", 20, 45);
					doc.setFontSize(12);
					doc.text(
						moment().format("DD/MM/YYYY"),
						doc.internal.pageSize.getWidth() - 60,
						45
					);

					isFirstPage = false;
				}

				doc.setFontSize(12);
				var pageSize = doc.internal.pageSize;
				var pageHeight = pageSize.height
					? pageSize.height
					: pageSize.getHeight();

				//footer
				doc.setFillColor(constants.PRIMARY);
				doc.rect(0, pageHeight - 16, pageSize.getWidth(), 16, "F");
				doc.setTextColor(255, 255, 255);
				doc.text(
					process.env.REACT_APP_NAME,
					data.settings.margin.left,
					pageHeight - 7
				);
				doc.text(
					`0328-7877378`,
					doc.internal.pageSize.getWidth() - 50,
					pageHeight - 7
				);
			},
			startY: 50,
		});

		if (isPrint) {
			doc.autoPrint();
			window.open(doc.output("bloburl"), "_blank");
		} else {
			doc.save("trial-balance.pdf");
		}
	};

	return (
		<>
			<Grid container sx={style.container}>
				<Grid item xs={9} sx={{ mb: 2 }}>
					<PageHeading text="Trial Balance" />
				</Grid>

				{arr?.length > 0 && (
					<Grid
						item
						xs={3}
						style={{
							display: "flex",
							flexDirection: "row",
							alignItems: "right",
							justifyContent: "flex-end",
						}}
					>
						<>
							<Button
								style={{
									margin: "-20px -10px 0 -10px",
								}}
								variant="text"
								text=""
								isShort={true}
								height={20}
								icon={<FontAwesomeIcon icon={faFileCsv} />}
								onClick={handleExport}
							></Button>

							<CSVLink
								ref={csvLinkRef}
								data={getCsvData()}
								filename={"trial-balance.csv"}
							></CSVLink>

							<Button
								style={{
									margin: "-20px -10px 0 -10px",
								}}
								variant="text"
								text=""
								isShort={true}
								height={20}
								icon={<FontAwesomeIcon icon={faFilePdf} />}
								onClick={() => createPdf(false)}
							></Button>

							<Button
								style={{
									margin: "-20px -10px 0 -10px",
								}}
								variant="text"
								text=""
								isShort={true}
								height={20}
								icon={<FontAwesomeIcon icon={faPrint} />}
								onClick={() => createPdf(true)}
							></Button>
						</>
					</Grid>
				)}

				<Grid item xs={12}>
					{isDone && (
						<CustomTable
							arr={arr}
							headers={headers}
							count={arr.length}
							page={page}
							isDone={isDone}
							setPage={setPage}
							rowsPerPage={rowsPerPage}
							setRowsPerPage={setRowsPerPage}
							showFooterSumRow={true}
							FooterSumRow={FooterSumRow}
						/>
					)}
				</Grid>
			</Grid>
		</>
	);
}

export default Trial;
